import React, { useState } from 'react';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Field, OptionButton, keepVisible } from '@/components/booking/fields';
import { LuxuryCalendar } from '@/components/booking/LuxuryCalendar';
import { ComparePackages } from '@/components/booking/ComparePackages';
import { Row } from '@/components/booking/landSteps';
import { TIME_WINDOWS, OCCUPANCY_OPTIONS, sizePackageFor, finalPackageFor } from '@/lib/realEstateOrder';
import { PACKAGES, packageIncludes } from '@/lib/realEstatePackages';
import { today, fmtDate } from '@/lib/bookFlow';
import { Check, Sparkles } from 'lucide-react';

const textareaCls = 'min-h-[88px] text-base bg-[#0f1724] border-white/15 text-white placeholder:text-white/30 focus:border-[#C6A14A]';
const labelOf = (list, v) => list.find((o) => o.value === v)?.label || v;

export const homePackage = (form) => PACKAGES[finalPackageFor(form)];

export const IncludesList = ({ pkg, testId }) => (
  <ul className="mt-2 space-y-1" data-testid={testId}>
    {packageIncludes(pkg).map((item) => (
      <li key={item} className="flex items-start gap-2 text-sm text-[#E8E4DE]/70">
        <Check className="h-3.5 w-3.5 mt-[3px] shrink-0 text-[#C6A14A]" strokeWidth={2.5} />{item}
      </li>
    ))}
  </ul>
);

const PackageCard = ({ pkg, badge, selected, onClick, testId }) => (
  <OptionButton selected={selected} onClick={onClick} testId={testId}>
    <div className="py-1.5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-medium flex items-center gap-1.5">{pkg.name}{badge}</p>
          <p className="text-xs text-[#C6A14A]/80 mt-0.5">{pkg.tagline}</p>
        </div>
        <p className="text-xl font-light whitespace-nowrap">${pkg.price}</p>
      </div>
      <IncludesList pkg={pkg} testId={`${testId}-includes`} />
    </div>
  </OptionButton>
);

const SecondChoice = ({ form, set }) => {
  const [open, setOpen] = useState(!!form.preferredDate2);
  if (!open) return <button type="button" onClick={() => setOpen(true)} className="text-sm text-[#C6A14A] py-2 min-h-[44px]" data-testid="book-add-second-choice">+ Add a second choice</button>;
  return (
    <div className="space-y-2 pt-2">
      <LuxuryCalendar id="home-date2" label="Second choice date (optional)" min={today()} value={form.preferredDate2} onChange={(v) => set('preferredDate2', v)} testId="book-calendar-2" />
      <div className="grid grid-cols-3 gap-2">
        {TIME_WINDOWS.map((t) => (
          <OptionButton key={t.value} selected={form.preferredTime2 === t.value} onClick={() => set('preferredTime2', t.value)} testId={`book-time2-${t.value}`}>
            <span className="text-xs">{t.label.split(' (')[0]}</span>
          </OptionButton>
        ))}
      </div>
    </div>
  );
};

export const buildHomeSteps = ({ form, set, ref, error }) => {
  const dateError = error && /date/i.test(error) ? error : null;
  const sqft = parseInt(form.squareFootage, 10) || 0;
  const size = sizePackageFor(sqft);
  return [
    {
      key: 'street', title: "What's the property address?",
      validate: () => (form.street.trim() ? null : 'Please enter the street address'),
      body: (
        <>
          <Field ref={ref} id="home-street" label="Street address" required autoComplete="address-line1" placeholder="123 Main Street" value={form.street} onChange={(e) => set('street', e.target.value)} />
          <Field id="home-unit" label="Unit / Suite" autoComplete="address-line2" placeholder="Apt 2B (optional)" value={form.unit} onChange={(e) => set('unit', e.target.value)} />
        </>
      ),
    },
    {
      key: 'location', title: 'City, state and ZIP',
      validate: () => (!form.city.trim() ? 'Please enter the city' : !/^[A-Za-z]{2}$/.test(form.state.trim()) ? 'Please enter the 2-letter state, e.g. AZ'
        : !/^\d{5}(-\d{4})?$/.test(form.zipCode.trim()) ? 'Please enter a 5-digit ZIP code' : null),
      body: (
        <>
          <Field ref={ref} id="home-city" label="City" required autoComplete="address-level2" placeholder="Kingman" value={form.city} onChange={(e) => set('city', e.target.value)} />
          <div className="grid grid-cols-2 gap-3">
            <Field id="home-state" label="State" required autoComplete="address-level1" placeholder="AZ" maxLength={2} value={form.state} onChange={(e) => set('state', e.target.value.toUpperCase())} />
            <Field id="home-zip" label="ZIP code" required inputMode="numeric" autoComplete="postal-code" placeholder="86401" maxLength={10} value={form.zipCode} onChange={(e) => set('zipCode', e.target.value)} />
          </div>
        </>
      ),
    },
    {
      key: 'sqft', title: 'How big is the home?', hint: 'Heated living space. This sets your package and price.',
      validate: () => (sqft > 0 ? null : 'Please enter the square footage, numbers only (e.g. 1850)'),
      body: <Field ref={ref} id="home-sqft" label="Square footage" required type="text" inputMode="numeric" autoComplete="off" placeholder="1850" value={form.squareFootage} onChange={(e) => set('squareFootage', e.target.value.replace(/\D/g, ''))} />,
    },
    {
      key: 'package', title: 'Choose your package',
      hint: size ? `Based on ${sqft.toLocaleString()} sq ft.` : `${PACKAGES.creator_spotlight.name} covers homes of any size, including yours.`,
      validate: () => null,
      body: (
        <>
          {size && (
            <PackageCard pkg={PACKAGES[size]}
              selected={!form.useCreatorSpotlight} onClick={() => set('useCreatorSpotlight', false)} testId="book-package-size" />
          )}
          <PackageCard pkg={PACKAGES.creator_spotlight} badge={<Sparkles className="h-4 w-4 text-[#C6A14A]" />}
            selected={form.useCreatorSpotlight || !size} onClick={() => set('useCreatorSpotlight', true)} testId="book-package-spotlight" />
          <ComparePackages pick={{
            current: finalPackageFor(form),
            isAvailable: (code) => code === 'creator_spotlight' || code === size,
            onSelect: (code) => set('useCreatorSpotlight', code === 'creator_spotlight'),
          }} />
        </>
      ),
    },
    {
      key: 'schedule', title: 'When should we shoot?', ownsError: !!dateError, hint: "Pick your first choice — we'll confirm the exact time.",
      validate: () => (!form.preferredDate1 ? 'Please pick a preferred date' : form.preferredDate1 < today() ? 'Please pick today or a later date'
        : !form.preferredTime1 ? 'Please pick a time window'
          : form.preferredDate2 && !form.preferredTime2 ? 'Please pick a time window for your second choice' : null),
      body: (
        <>
          <LuxuryCalendar id="home-date1" label="Preferred date" required min={today()} value={form.preferredDate1} onChange={(v) => set('preferredDate1', v)} error={dateError} testId="book-calendar" />
          <div className="space-y-2" role="radiogroup" aria-label="Preferred time window">
            <Label className="text-[#E8E4DE]/80 text-sm">Time window<span className="text-[#C6A14A]"> *</span></Label>
            {TIME_WINDOWS.map((t) => (
              <OptionButton key={t.value} selected={form.preferredTime1 === t.value} onClick={() => set('preferredTime1', t.value)} testId={`book-time1-${t.value}`}>{t.label}</OptionButton>
            ))}
          </div>
          <SecondChoice form={form} set={set} />
        </>
      ),
    },
    {
      key: 'access', title: 'How do we get in?',
      validate: () => (form.occupancy ? null : 'Please choose who will be at the property'),
      body: (
        <>
          <div className="space-y-2" role="radiogroup" aria-label="Occupancy status">
            <Label className="text-[#E8E4DE]/80 text-sm">Who will be at the property?<span className="text-[#C6A14A]"> *</span></Label>
            {OCCUPANCY_OPTIONS.map((o) => (
              <OptionButton key={o.value} selected={form.occupancy === o.value} onClick={() => set('occupancy', o.value)} testId={`book-occupancy-${o.value}`}>{o.label}</OptionButton>
            ))}
          </div>
          <Field id="home-lockbox" label="Lockbox code" placeholder="Optional" autoComplete="off" value={form.lockboxCode} onChange={(e) => set('lockboxCode', e.target.value)} hint="Only visible to you and your photographer." />
          <div className="space-y-1.5">
            <Label htmlFor="home-access-notes" className="text-[#E8E4DE]/80 text-sm">Access notes</Label>
            <Textarea id="home-access-notes" onFocus={keepVisible} placeholder="Gate code, parking, meet at the property…" value={form.accessNotes} onChange={(e) => set('accessNotes', e.target.value)} className={textareaCls} data-testid="book-home-access-notes" />
          </div>
        </>
      ),
    },
  ];
};

export const HomeSummary = ({ form, set }) => {
  const pkg = homePackage(form);
  return (
    <>
      <div className="rounded-xl border border-white/10 bg-[#0f1724] px-4" data-testid="home-summary">
        <Row k="Property" v={`${form.street}${form.unit ? `, ${form.unit}` : ''}, ${form.city}, ${form.state} ${form.zipCode}`} />
        <Row k="Size" v={`${(parseInt(form.squareFootage, 10) || 0).toLocaleString()} sq ft`} />
        <Row k="Package" v={`${pkg.name} — $${pkg.price}`} />
        <div className="py-2.5 border-b border-white/5">
          <span className="text-[#E8E4DE]/50 text-sm">Includes</span>
          <IncludesList pkg={pkg} testId="home-summary-includes" />
        </div>
        <Row k="First choice" v={`${fmtDate(form.preferredDate1)} · ${labelOf(TIME_WINDOWS, form.preferredTime1)}`} />
        {form.preferredDate2 && form.preferredTime2 && <Row k="Second choice" v={`${fmtDate(form.preferredDate2)} · ${labelOf(TIME_WINDOWS, form.preferredTime2)}`} />}
        <Row k="Occupancy" v={labelOf(OCCUPANCY_OPTIONS, form.occupancy)} />
        {form.lockboxCode && <Row k="Lockbox" v="Provided" />}
        {form.accessNotes && <Row k="Access notes" v={form.accessNotes} />}
      </div>
      <p className="text-sm text-[#E8E4DE]/60" data-testid="home-invoice-note">No payment now — you'll get an invoice after the shoot.</p>
      <div className="space-y-1.5">
        <Label htmlFor="home-comments" className="text-[#E8E4DE]/80 text-sm">Anything else? (optional)</Label>
        <Textarea id="home-comments" onFocus={keepVisible} placeholder="Special requests or notes…" value={form.comments} onChange={(e) => set('comments', e.target.value)} className={textareaCls} data-testid="book-home-comments" />
      </div>
    </>
  );
};
